import { normalizeSearch, type ConversationView, type SearchResult } from '@swchat/client-sdk';
import { useEffect, useRef, type KeyboardEvent } from 'react';
import { Avatar, Icon, Modal } from '../design';
import { useClient } from '../lib/hooks';
import { shortTime } from './format';

/** Số hội thoại khớp tên tối đa hiển thị trong bảng tìm kiếm. */
const MAX_CONVERSATIONS = 6;

/** Hội thoại có tên khớp `query` (không phân biệt dấu, như D1) — chỉ dữ liệu cục bộ. */
export function matchConversations(conversations: readonly ConversationView[], query: string): ConversationView[] {
  const q = normalizeSearch(query.trim());
  if (!q) return [];
  return conversations.filter((c) => normalizeSearch(c.title).includes(q)).slice(0, MAX_CONVERSATIONS);
}

/**
 * Bảng tìm kiếm toàn cục (⌘K / Ctrl+K, W1.6): tên hội thoại + tin nhắn (D1, `client.search` trên store cục bộ đã giải
 * mã — không gửi gì lên server). Giữ `data-testid` của ô tìm kiếm cũ (`search-input`, `search-only-current`,
 * `search-results`, `search-result`). ↑/↓ di chuyển giữa kết quả, Enter mở, Esc đóng (Modal).
 */
export function SearchPalette({
  conversations,
  current,
  query,
  onlyCurrent,
  results,
  onQuery,
  onOnlyCurrent,
  onOpenConversation,
  onOpenResult,
  onClose,
}: {
  conversations: ConversationView[];
  /** Hội thoại đang mở (cho tùy chọn "Chỉ trong …"). */
  current?: ConversationView;
  query: string;
  onlyCurrent: boolean;
  results: SearchResult[];
  onQuery: (q: string) => void;
  onOnlyCurrent: (v: boolean) => void;
  onOpenConversation: (id: string) => void;
  onOpenResult: (r: SearchResult) => void;
  onClose: () => void;
}) {
  const client = useClient();
  const list = useRef<HTMLUListElement>(null);
  const input = useRef<HTMLInputElement>(null);
  // Mở lại với từ khóa lần trước: chọn sẵn toàn bộ để gõ đè (Modal đã đặt focus vào ô tìm — effect của con chạy trước).
  useEffect(() => input.current?.select(), []);
  const q = query.trim();
  const convs = onlyCurrent && current ? [] : matchConversations(conversations, q);
  const titleOf = (id: string) => conversations.find((c) => c.id === id)?.title ?? '…';

  const items = () => [...(list.current?.querySelectorAll<HTMLElement>('[data-palette-item]') ?? [])];
  function onKeyDown(e: KeyboardEvent) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const all = items();
    if (!all.length) return;
    e.preventDefault();
    const i = all.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % all.length : i <= 0 ? all.length - 1 : i - 1;
    all[next]!.focus();
  }

  return (
    <Modal title="Tìm kiếm" size="palette" testId="search-palette" onClose={onClose}>
      <div className="ws-palette" onKeyDown={onKeyDown}>
        <label className="ws-search">
          <Icon name="search" size={16} />
          <input
            ref={input}
            type="search"
            data-testid="search-input"
            aria-label="Tìm hội thoại và tin nhắn"
            placeholder="Tìm hội thoại, tin nhắn…"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            onKeyDown={(e) => {
              // Enter trong ô tìm → mở kết quả đầu tiên.
              if (e.key === 'Enter') {
                e.preventDefault();
                items()[0]?.click();
              }
            }}
          />
        </label>
        {q && current && (
          <label className="ws-check ws-small ws-muted">
            <input type="checkbox" data-testid="search-only-current" checked={onlyCurrent} onChange={(e) => onOnlyCurrent(e.target.checked)} />
            Chỉ trong “{current.title}”
          </label>
        )}
        {!q ? (
          <p className="ws-palette-hint">Tìm theo tên hội thoại hoặc nội dung tin nhắn đã giải mã trên thiết bị này (không phân biệt dấu).</p>
        ) : (
          <ul className="ws-palette-list" ref={list} aria-label="Kết quả tìm kiếm">
            {convs.length > 0 && (
              <>
                <li className="ws-palette-group" aria-hidden="true">
                  Hội thoại
                </li>
                {convs.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="ws-palette-conv"
                      data-palette-item
                      data-testid="search-conv-result"
                      data-conv-id={c.id}
                      onClick={() => onOpenConversation(c.id)}
                    >
                      <Avatar name={c.title} seed={c.id} size="sm" />
                      <span className="ws-grow ws-ellipsis">{c.title}</span>
                      <span className="ws-muted ws-small">{c.kind === 'direct' ? 'Chat 1-1' : `${c.members.length} thành viên`}</span>
                    </button>
                  </li>
                ))}
              </>
            )}
            <li className="ws-palette-group" aria-hidden="true">
              Tin nhắn
            </li>
            <li>
              <ul className="ws-palette-sublist" data-testid="search-results">
                {results.map((r) => (
                  <li key={`${r.conversation_id}:${r.message_id}`}>
                    <button
                      type="button"
                      className="ws-result"
                      data-palette-item
                      data-testid="search-result"
                      data-conv-id={r.conversation_id}
                      data-msg-id={r.message_id}
                      onClick={() => onOpenResult(r)}
                    >
                      <span className="ws-conv-line">
                        <strong className="ws-conv-title">{titleOf(r.conversation_id)}</strong>
                        <time className="ws-conv-time">{shortTime(r.ts)}</time>
                      </span>
                      <span className="ws-conv-preview">
                        {client.getUser(r.sender_user_id)?.display_name ?? '…'}: {r.snippet}
                      </span>
                    </button>
                  </li>
                ))}
                {!results.length && <li className="ws-muted ws-empty">Không tìm thấy tin nhắn trên thiết bị này.</li>}
              </ul>
            </li>
          </ul>
        )}
      </div>
    </Modal>
  );
}
